import { useState, useEffect } from "react";
import { ChevronRight, ChevronLeft, ListChecks, Sparkles } from "lucide-react";
import api, { formatErr } from "../lib/api";
import MicButton from "./MicButton";
import StageRailItem from "./StageRailItem";
import { RAIL_STATUS_META } from "./stageRailUtils";
import { useT } from "../lib/i18n";
import useDownloadableModules from "../lib/useDownloadableModules";

const STATUS_META = RAIL_STATUS_META;

/** Wave V — collapsible stage-gate rail (always visible across all tabs).
 *
 *   Props:
 *     data, err              — from useStageGate hook
 *     activeCode, onPick     — controlled active stage selector
 *     toggleApprovalGates    — from useStageGate hook
 *     refresh                — from useStageGate hook
 *     projectId              — for free-form prompt routing
 *     collapsed, onToggle    — collapse-rail UX (icons-only when collapsed)
 */
export default function StageGateRail({
  data, err, activeCode, onPick,
  toggleApprovalGates, refresh, projectId,
  collapsed = false, onToggle,
  phaseVisibility = null,    // Wave AG — {stages: {code: {state, note, target_pct}}, phase}
  subTypeLabel = null,        // Resolved sub-industry name (e.g., "LNG Liquefaction")
  projectName = null,         // Project's display name for context
  domain = null,              // Wave CG — project domain (for PP overlay)
  subIndustry = null,         // Wave CG — project sub_industry key (pharma / lng / semicon …)
}) {
  const t = useT();
  const [routerPrompt, setRouterPrompt] = useState("");
  const [routing, setRouting] = useState(false);
  const [routedTo, setRoutedTo] = useState(null);
  const [routerErr, setRouterErr] = useState("");
  // Per-module XLSX template downloads — populated only for projects
  // whose sub_type has stage_gate_config mappings.
  const { downloadableModules } = useDownloadableModules(projectId);
  // File 47 — Required vs Optional filter (only meaningful for dynamic infra/RE configs).
  const [requiredOnly, setRequiredOnly] = useState(false);

  // ── Sprint 2 Turn 10 — per-code gate status (colored dots) ───────
  // Map: code → {has_output, has_errors, blocked, blocked_by[], runnable, overridden}
  // Sourced from GET /infra/projects/{pid}/gate-status. Re-fetches on
  // mount and on every "rachana.stage.completed" window event (fired
  // by InfraStagePanel after a successful runStage).
  const [gateStatus, setGateStatus] = useState({});

  // ── Wave CG — Process-plant sub-sector overlay (P3 frontend wiring) ──
  // Visible only when domain === "process_plant" AND the sub_industry has
  // a registered overlay (pharma · lng · semicon · cement · …).
  //
  // If the parent didn't supply `domain` / `subIndustry` (e.g. project
  // state is still loading or the parent had a transient fetch error),
  // fall back to fetching the project ourselves via `projectId`.
  const [ppOverlay, setPpOverlay] = useState(null);
  // CH.3 — sub-sector overlay banner is collapsed by default; user
  // can expand it to see added/N-A/promoted code details.
  const [ppOverlayOpen, setPpOverlayOpen] = useState(false);
  const [resolvedSub, setResolvedSub] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      let d = domain;
      let s = subIndustry;
      if ((!d || !s) && projectId) {
        try {
          const { data: p } = await api.get(`/projects/${projectId}`);
          d = d || p?.domain;
          s = s || p?.sub_industry || p?.sub_industry_key
                || p?.sub_type || p?.industry;
        } catch (_e) {
          // project fetch failed — silently abort overlay
        }
      }
      if (cancelled) return;
      if (d !== "process_plant" || !s) {
        setPpOverlay(null);
        setResolvedSub(null);
        return;
      }
      setResolvedSub(s);
      try {
        const { data: o } = await api.get(
          `/industry/pp-overlay/${encodeURIComponent(s)}`);
        const hasExtras = !!(o && (
          Object.keys(o.extra_deliverables || {}).length
          || (o.required_promotions || []).length
          || (o.n_a_codes || []).length
        ));
        if (!cancelled) setPpOverlay(hasExtras ? o : null);
      } catch (_e) {
        if (!cancelled) setPpOverlay(null);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [domain, subIndustry, projectId]);
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get(
          `/infra/projects/${projectId}/gate-status`);
        if (!cancelled) setGateStatus(data?.results || {});
      } catch (_e) {
        if (!cancelled) setGateStatus({});
      }
    };
    fetchStatus();
    const handler = () => fetchStatus();
    window.addEventListener("rachana.stage.completed", handler);
    // Phase 5 — dependency-chip clicks dispatch a `rachana.stage.pick`
    // event so we can route the rail's active code without prop drilling.
    const pickHandler = (e) => {
      const code = e?.detail?.code;
      if (code) onPick?.(code);
    };
    window.addEventListener("rachana.stage.pick", pickHandler);
    return () => {
      cancelled = true;
      window.removeEventListener("rachana.stage.completed", handler);
      window.removeEventListener("rachana.stage.pick", pickHandler);
    };
  }, [projectId, onPick]);

  const sendFreePrompt = async (overrideText) => {
    const used = (overrideText ?? routerPrompt).trim();
    if (!used) return;
    setRouting(true); setRoutedTo(null); setRouterErr("");
    try {
      const { data: r } = await api.post(`/projects/${projectId}/prompt`,
                                                          { prompt: used });
      setRoutedTo(r.routed_to);
      onPick?.(r.routed_to);
      setRouterPrompt("");
      refresh?.();
    } catch (e) {
      setRouterErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRouting(false); }
  };

  if (err) {
    return <div className="pf-sev pf-sev-error"
                      style={{ display: "block", padding: 12, width: 280 }}>{err}</div>;
  }
  if (!data) {
    return <div style={{ padding: 16, color: "var(--pf-text-muted)",
                                  width: collapsed ? 48 : 280, fontSize: 12 }}>Loading stage gate…</div>;
  }

  const _flatten = (arr, out = []) => {
    (arr || []).forEach((s) => {
      out.push(s);
      if (s.sub_stages?.length) _flatten(s.sub_stages, out);
    });
    return out;
  };
  const flatList = data.stages_flat || _flatten(data.stages);
  // P0 — Feb 2026 fix: merge live gate-status into flatByCode so the
  // rail's status resolver (`effectiveStatus`) treats stages with a
  // successful run (`has_output`) or explicit user approval
  // (`approved`) as `complete` / `approved`. Without this the workspace
  // API still reports `status: "not_started"` for L3 codes the user has
  // actually run/approved, so `effectiveStatus` falls back to "locked"
  // even when the gate dot is green — that's why approved A.1.2 / A.1.3
  // / … kept showing a 🔒 icon next to a ✅ dot.
  const flatByCode = Object.fromEntries(flatList.map((s) => {
    const g = gateStatus?.[s.code];
    if (!g) return [s.code, s];
    // Only escalate status — never downgrade a status the catalogue
    // already set (e.g. user manually marked "in_progress").
    let nextStatus = s.status;
    if (g.approved && nextStatus !== "approved") nextStatus = "approved";
    else if (g.has_output && !g.has_errors
              && !["approved", "complete"].includes(nextStatus)) {
      nextStatus = "complete";
    } else if (g.has_errors && nextStatus !== "approved") {
      nextStatus = "in_progress";
    }
    return [s.code, { ...s, status: nextStatus }];
  }));

  // File 47 — sub-sector banner data (only present for dynamic configs).
  const isDynamic = !!data.use_dynamic;
  const subSectorLabel = data.display_name || (data.sub_type || "").replace(/_/g, " ");
  const totalStages = data.total_stages;
  const totalModules = data.total_modules;
  // Count required vs optional from catalogue (only for L2-level codes).
  const reqCount = (data.catalogue || [])
    .filter((s) => s.code.includes(".") && s.required !== false).length;
  const optCount = (data.catalogue || [])
    .filter((s) => s.code.includes(".") && s.required === false).length;

  // Apply Required filter: prune sub_stages whose `required === false`.
  const filterRequired = (stage) => {
    if (!requiredOnly) return stage;
    return {
      ...stage,
      sub_stages: (stage.sub_stages || [])
        .filter((s) => s.required !== false)
        .map(filterRequired),
    };
  };
  // Pick the hierarchical source that actually carries `sub_stages`:
  //  • Dynamic / File-47 mode (infra & real-estate sub-sectors):
  //    `useStageGate` builds `data.stages` as a nested tree — each L1 already
  //    carries `sub_stages` (the L2 modules).
  //  • Legacy mode (process_plant): `data.stages` is FLAT (L1+L2+L3 with
  //    parent_code), but `data.catalogue` keeps the nested form with
  //    `sub_stages` per L1.
  // Use `stages` whenever any of its top-level entries already has children;
  // otherwise fall back to `catalogue`. This is the same heuristic that was
  // working pre-refactor for both modes.
  const _stagesHaveSubs = (data.stages || []).some(
    (s) => Array.isArray(s.sub_stages) && s.sub_stages.length > 0,
  );
  const _hierSource = _stagesHaveSubs ? data.stages : (data.catalogue || []);
  const top = _hierSource
    .filter((s) => !s.code.includes("."))
    .map(filterRequired);

  return (
    <aside data-testid="stage-gate-rail"
        style={{ width: collapsed ? 48 : 320, flexShrink: 0,
                      borderRight: "1px solid var(--pf-border)",
                      overflow: "hidden", display: "flex", flexDirection: "column",
                      background: "var(--pf-surface)",
                      transition: "width 0.18s ease" }}>
      {/* ── Upper region: project header + sub-sector overlay + phase
            indicator + approval gates + Ask Anything.
            Has its OWN scroll, independent of the stage tree below. ── */}
      <div data-testid="stage-rail-upper"
          style={{ flexShrink: 0,
                        maxHeight: collapsed ? "none" : "55%",
                        overflowY: collapsed ? "visible" : "auto",
                        display: "flex", flexDirection: "column",
                        borderBottom: collapsed ? "none" : "2px solid var(--pf-border)" }}>
      <div style={{ padding: collapsed ? "14px 8px" : "14px 14px 10px",
                          borderBottom: "1px solid var(--pf-border)",
                          display: "flex", flexDirection: collapsed ? "row" : "column",
                          gap: collapsed ? 8 : 6,
                          alignItems: collapsed ? "center" : "stretch",
                          justifyContent: collapsed ? "center" : "flex-start" }}>
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 8,
                        justifyContent: "space-between" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <ListChecks size={16} color="var(--pf-spark)" />
              <span className="pf-overline" style={{ margin: 0 }}>
                STAGE GATE · {data.domain.replace(/_/g, " ").toUpperCase()}
              </span>
            </span>
            <button data-testid="stage-rail-collapse"
                onClick={onToggle}
                style={{ background: "transparent", border: "none",
                              color: "var(--pf-text-muted)", cursor: "pointer",
                              padding: 4 }}
                title="Collapse stage gate">
              <ChevronLeft size={16} />
            </button>
          </div>
        )}
        {collapsed && (
          <button data-testid="stage-rail-collapse"
              onClick={onToggle}
              style={{ background: "transparent", border: "none",
                            color: "var(--pf-text-muted)", cursor: "pointer",
                            padding: 4 }}
              title="Expand stage gate">
            <ChevronRight size={16} />
          </button>
        )}
        {!collapsed && (subTypeLabel && subTypeLabel !== "—") && (
          <div data-testid="stage-rail-subtype"
               title={subTypeLabel}
               style={{ fontSize: 13, fontWeight: 700,
                        color: "var(--pf-spark)",
                        lineHeight: 1.35, paddingLeft: 24,
                        wordBreak: "break-word" }}>
            {subTypeLabel}
          </div>
        )}
        {!collapsed && projectName && (
          <div data-testid="stage-rail-project-name"
               title={projectName}
               style={{ fontSize: 11, color: "var(--pf-text-muted)",
                        paddingLeft: 24, lineHeight: 1.3,
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap" }}>
            {projectName}
          </div>
        )}
      </div>

      {/* File 47 — sub-sector banner (only shown for dynamic infra/RE configs) */}
      {!collapsed && isDynamic && subSectorLabel && (
        <div data-testid="rail-subsector-banner"
            style={{ padding: "10px 14px",
                        borderBottom: "1px solid var(--pf-border)",
                        background: "linear-gradient(90deg, rgba(245, 158, 11, 0.08), transparent)",
                        display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%",
                                background: "var(--pf-accent)" }} />
            <span data-testid="rail-subsector-name"
                style={{ fontSize: 13, fontWeight: 700,
                            color: "var(--pf-text)",
                            textTransform: "capitalize" }}>
              {subSectorLabel}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8,
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 10.5, color: "var(--pf-text-muted)" }}>
            <span data-testid="rail-stage-count">{totalStages} stages</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span data-testid="rail-module-count">{totalModules} modules</span>
            {(reqCount + optCount) > 0 && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span title="Required modules"
                    style={{ color: "var(--pf-spark)" }}>
                  {reqCount} req
                </span>
                {optCount > 0 && (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span title="Optional modules"
                        style={{ color: "var(--pf-text-dim)" }}>
                      {optCount} opt
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          {optCount > 0 && (
            <button type="button"
                data-testid="rail-required-only-toggle"
                onClick={() => setRequiredOnly((v) => !v)}
                style={{ background: requiredOnly ? "var(--pf-accent)" : "transparent",
                              border: `1px solid ${requiredOnly ? "var(--pf-accent)" : "var(--pf-border)"}`,
                              color: requiredOnly ? "#fff" : "var(--pf-text-muted)",
                              fontSize: 10, padding: "3px 8px",
                              borderRadius: 4, cursor: "pointer",
                              alignSelf: "flex-start",
                              marginTop: 4,
                              fontFamily: "'JetBrains Mono', monospace",
                              letterSpacing: 0.4 }}
                title={requiredOnly
                    ? "Showing required modules only — click to show all"
                    : "Show only required modules (hide optional/contingent)"}>
              {requiredOnly ? `✓ REQUIRED ONLY · HIDING ${optCount}` : "REQUIRED ONLY"}
            </button>
          )}
        </div>
      )}

      {/* Wave CG — Process-plant sub-sector overlay banner (P3 frontend
            wiring). Visible only for pharma · lng · semicon · cement · …
            where the backend `/industry/pp-overlay/{sub}` API reports
            extras / N/A codes / promotions. Lists the added codes
            grouped by stage and highlights N/A codes — making it
            obvious to users which deliverables the workflow added or
            removed for their specific sub-sector. */}
      {!collapsed && ppOverlay && (() => {
        const extras = ppOverlay.extra_deliverables || {};
        const naCodes = ppOverlay.n_a_codes || [];
        const promotions = ppOverlay.required_promotions || [];
        const allAddedCodes = Object.values(extras)
          .flatMap((arr) => arr || []).map((e) => e.code);
        const stageKeys = Object.keys(extras).sort();
        return (
          <div data-testid="pp-overlay-banner"
              style={{ borderBottom: "1px solid var(--pf-border)",
                          background: "linear-gradient(90deg, rgba(15, 98, 254, 0.18), rgba(15, 98, 254, 0.04))",
                          position: "sticky", top: 0, zIndex: 5,
                          backdropFilter: "blur(6px)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
            <button type="button"
                data-testid="pp-overlay-toggle"
                onClick={() => setPpOverlayOpen((v) => !v)}
                style={{ background: "transparent", border: "none",
                              padding: "10px 14px", cursor: "pointer",
                              display: "flex", alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%", color: "var(--pf-text)" }}
                title="Toggle sub-sector overlay summary">
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%",
                                      background: "#0F62FE" }} />
                <span data-testid="pp-overlay-title"
                    style={{ fontSize: 12, fontWeight: 700,
                                  color: "var(--pf-text)",
                                  fontFamily: "'JetBrains Mono', monospace",
                                  letterSpacing: 0.4,
                                  textTransform: "uppercase" }}>
                  {(ppOverlay.resolved_key || subIndustry || "PP")
                      .replace(/_/g, " ")} overlay
                </span>
              </span>
              <span data-testid="pp-overlay-counts"
                  style={{ fontSize: 10, color: "var(--pf-text-muted)",
                                fontFamily: "'JetBrains Mono', monospace" }}>
                +{allAddedCodes.length} added · {naCodes.length} N/A ·{" "}
                {promotions.length} promoted
              </span>
            </button>
            {ppOverlayOpen && (
              <div data-testid="pp-overlay-body"
                  style={{ padding: "0 14px 12px",
                                fontSize: 11.5, color: "var(--pf-text)",
                                fontFamily: "'JetBrains Mono', monospace" }}>
                {ppOverlay.notes && (
                  <div data-testid="pp-overlay-notes"
                      style={{ color: "var(--pf-text-muted)",
                                    marginBottom: 8, lineHeight: 1.45 }}>
                    {ppOverlay.notes}
                  </div>
                )}
                {stageKeys.map((sk) => (
                  <div key={sk}
                      data-testid={`pp-overlay-stage-${sk}`}
                      style={{ marginBottom: 6 }}>
                    <div style={{ color: "#0F62FE", fontWeight: 700,
                                          letterSpacing: 0.4 }}>
                      Stage {sk}
                    </div>
                    {(extras[sk] || []).map((e) => (
                      <button key={e.code} type="button"
                          data-testid={`pp-overlay-code-${e.code}`}
                          onClick={() => onPick?.(e.code.split(".")
                              .slice(0, 2).join("."))}
                          title={`Jump to ${e.code}`}
                          style={{ display: "flex", alignItems: "center",
                                        gap: 6, padding: "2px 0",
                                        background: "transparent",
                                        border: "none", cursor: "pointer",
                                        color: "var(--pf-text)",
                                        fontFamily: "'JetBrains Mono', monospace",
                                        fontSize: 11, textAlign: "left",
                                        width: "100%" }}>
                        <span style={{ color: "#0F62FE", minWidth: 50 }}>
                          + {e.code}
                        </span>
                        <span style={{ flex: 1,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap" }}>
                          {e.name}
                        </span>
                        {e.required && (
                          <span style={{ color: "var(--pf-spark)",
                                                fontSize: 9, marginLeft: 4 }}>
                            REQ
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
                {naCodes.length > 0 && (
                  <div data-testid="pp-overlay-na-list"
                      style={{ marginTop: 6,
                                    paddingTop: 6,
                                    borderTop: "1px dashed var(--pf-border)" }}>
                    <div style={{ color: "#A36B17", fontWeight: 700,
                                          letterSpacing: 0.4 }}>
                      N/A for {(ppOverlay.resolved_key || "").replace(/_/g, " ")}
                    </div>
                    {naCodes.map((c) => (
                      <div key={c}
                          data-testid={`pp-overlay-na-${c}`}
                          style={{ display: "flex", gap: 6,
                                        color: "var(--pf-text-muted)",
                                        textDecoration: "line-through" }}>
                        <span style={{ minWidth: 50 }}>– {c}</span>
                      </div>
                    ))}
                  </div>
                )}
                {promotions.length > 0 && (
                  <div data-testid="pp-overlay-prom-list"
                      style={{ marginTop: 6,
                                    paddingTop: 6,
                                    borderTop: "1px dashed var(--pf-border)" }}>
                    <div style={{ color: "var(--pf-spark)", fontWeight: 700,
                                          letterSpacing: 0.4 }}>
                      Promoted to required
                    </div>
                    {promotions.map((c) => (
                      <div key={c}
                          data-testid={`pp-overlay-prom-${c}`}
                          style={{ color: "var(--pf-text)" }}>
                        ★ {c}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Phase indicator + Approval gates + Ask Anything are part of
            the UPPER scroll region (independent of the stage tree). */}
      {!collapsed && phaseVisibility?.phase && (
        <div data-testid="rail-phase-indicator"
              style={{ padding: "10px 14px",
                        borderBottom: "1px solid var(--pf-border)",
                        background: "var(--pf-surface-2)",
                        display: "flex", alignItems: "center", gap: 8,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11 }}>
          <span style={{ color: "var(--pf-text-dim)" }}>PHASE:</span>
          <span style={{ color: "var(--pf-accent)", fontWeight: 700,
                            letterSpacing: 0.4 }}>
            {phaseVisibility.phase.replace("_", "-").toUpperCase()}
          </span>
          <span style={{ flex: 1, textAlign: "right",
                            color: "var(--pf-text-dim)", fontSize: 10 }}>
            {(() => {
              const cells = Object.values(phaseVisibility.stages || {});
              const act = cells.filter(c => c.state === "active").length;
              const part = cells.filter(c => c.state === "partial").length;
              const lock = cells.filter(c => c.state === "locked").length;
              return `${act} active · ${part} partial · ${lock} locked`;
            })()}
          </span>
        </div>
      )}

      {!collapsed && (
        <button type="button" onClick={toggleApprovalGates}
            style={{ background: "transparent", border: "none",
                          padding: "10px 14px", cursor: "pointer",
                          borderBottom: "1px solid var(--pf-border)",
                          display: "flex", alignItems: "center", gap: 10,
                          color: "var(--pf-text)" }}
            data-testid="approval-gates-toggle">
          <span style={{ width: 28, height: 14, borderRadius: 7,
                                background: data.approval_gates_enabled ? "var(--pf-accent)" : "var(--pf-border)",
                                position: "relative", transition: "background .2s" }}>
            <span style={{ position: "absolute", top: 1,
                                  left: data.approval_gates_enabled ? 15 : 1,
                                  width: 12, height: 12, borderRadius: "50%",
                                  background: "white", transition: "left .2s" }} />
          </span>
          <span style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
            Approval gates {data.approval_gates_enabled ? "ON" : "OFF"}
          </span>
        </button>
      )}

      {!collapsed && (
        <div style={{ padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center",
                              justifyContent: "space-between", marginBottom: 6 }}>
            <div className="pf-overline">{t("ask_anything", "ASK ANYTHING")}</div>
            <MicButton testId="free-prompt-mic" size="sm"
                onTranscript={(t2) => setRouterPrompt(prev => prev ? prev + " " + t2 : t2)}
                onAutoSubmit={(t2) => { setRouterPrompt(t2); sendFreePrompt(t2); }} />
          </div>
          <textarea className="pf-input pf-textarea" rows={2}
              placeholder="e.g. resize the column for 99.5% benzene · hold Space to dictate"
              value={routerPrompt}
              onChange={(e) => setRouterPrompt(e.target.value)}
              data-testid="free-prompt-input"
              style={{ fontSize: 12, padding: 8 }} />
          <button className="btn btn-ghost"
              style={{ marginTop: 6, width: "100%" }}
              disabled={routing || !routerPrompt.trim()}
              onClick={() => sendFreePrompt()}
              data-testid="free-prompt-submit">
            <Sparkles size={13} /> {routing ? "Routing…" : t("send", "Send")}
          </button>
          {routedTo && <div style={{ marginTop: 6, fontSize: 11,
                                        color: "var(--pf-spark)",
                                        fontFamily: "'JetBrains Mono', monospace" }}>
              Routed → Stage {routedTo}
            </div>}
          {routerErr && <div style={{ marginTop: 6, fontSize: 11,
                                          color: "#EF4444" }}>{routerErr}</div>}
        </div>
      )}
      </div>
      {/* ── End upper region. Below is the LOWER scroll region (stage tree). ── */}

      {/* Collapsed mode: only shows code badges as a vertical strip. */}
      {collapsed ? (
        <div style={{ flex: 1, padding: "8px 4px", overflowY: "auto",
                            display: "flex", flexDirection: "column", gap: 2 }}
             data-testid="stage-rail-lower">
          {top.map((s) => {
            const meta = STATUS_META[(flatByCode[s.code] || {}).status] || STATUS_META.not_started;
            const Icon = meta.Icon;
            const isActive = activeCode === s.code;
            const phCell = phaseVisibility?.stages?.[s.code];
            const phState = phCell?.state || "active";
            return (
              <button key={s.code}
                  onClick={() => onPick(s.code)}
                  data-testid={`rail-collapsed-${s.code}`}
                  data-phase-state={phState}
                  title={`${s.code} · ${s.name}${phCell ? ` — ${phCell.state.toUpperCase()}: ${phCell.note}` : ""}`}
                  style={{ background: isActive ? "var(--pf-surface-2)" : "transparent",
                              border: "none", padding: "8px 4px", cursor: "pointer",
                              display: "flex", flexDirection: "column",
                              alignItems: "center", gap: 2,
                              opacity: phState === "locked" ? 0.35 : (phState === "partial" ? 0.75 : 1),
                              borderLeft: isActive ? "2px solid var(--pf-spark)"
                                          : (phState === "locked" ? "2px solid transparent"
                                             : (phState === "partial" ? "2px solid #eab308" : "2px solid transparent")) }}>
                <Icon size={11} color={meta.color} />
                <span style={{ fontFamily: "'JetBrains Mono', monospace",
                                      fontSize: 10, color: "var(--pf-spark)" }}>
                  {s.code}
                </span>
                {phCell && phState !== "active" && (
                  <span style={{ fontSize: 8, color: phState === "locked" ? "var(--pf-text-dim)" : "#eab308", fontFamily: "'JetBrains Mono', monospace" }}>
                    {phState === "locked" ? "⬜" : "🔶"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div data-testid="stage-rail-lower"
             style={{ flex: 1, overflowY: "auto", paddingBottom: 16,
                          minHeight: 0 }}>
          {top.map((s) => (
            <StageRailItem key={s.code} stage={s}
                flatByCode={flatByCode} catalogue={data.catalogue}
                activeCode={activeCode} onPick={onPick}
                gateStatus={gateStatus}
                phaseVisibility={phaseVisibility}
                projectId={projectId}
                downloadableModules={downloadableModules} />
          ))}
        </div>
      )}
    </aside>
  );
}
